import React, { useState, useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import './App.css';
import './lib/preloadCritical'; // injects high-priority preload hints
import { installBfcacheImageRecovery } from './lib/bfcacheImageRecovery';
installBfcacheImageRecovery(); // recover blank images after Safari bfcache restore
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import CustomCursor from './components/CustomCursor';
import Loader from './components/Loader';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import HeroDemoStrip from './components/HeroDemoStrip';
import Marquee from './components/Marquee';
import Stats from './components/Stats';
import About from './components/About';
import Classes from './components/Classes';
import Trainers from './components/Trainers';
import Memberships from './components/Memberships';
import MemberApp from './components/MemberApp';
import Testimonials from './components/Testimonials';
import CTA from './components/CTA';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WebsiteFx from './components/WebsiteFx';
import BottomVideo from './components/BottomVideo';
import KidCareBanner from './components/KidCareBanner';
import SectionIndex from './components/SectionIndex';
import MembershipCheckout from './components/MembershipCheckout';
import MembershipInfo from './components/MembershipInfo';
import EmberField from './components/EmberField';
import ActiveAdults from './components/ActiveAdults';
import LazyMount from './components/LazyMount';
import { runNavigationIntegrityCheck } from './dev/navigationIntegrity';
import { smoothScrollToHash, cancelActiveScroll, isScrolling } from './lib/smoothScroll';
import useReveal from './hooks/useReveal';
import { Toaster } from 'sonner';

// Code-split: only loaded when actually needed.
const VideoModal = lazy(() => import('./components/VideoModal'));

// === Eager prefetch of checkout chunks ===
// Membership routes are statically imported (chunks total ~18 KB minified,
// far less than the cost of a Suspense round-trip on every click).
// prefetchCheckout is kept as a no-op export for backwards compatibility
// with the pricing-card hover handlers.
export function prefetchCheckout() { /* no-op — bundled into main */ }

// === CLEAN URL → SECTION ID MAP ===
// Every entry here becomes a shareable, bookmarkable, canonical URL that
// resolves to the homepage AND auto-scrolls to the right section on first
// paint. Direct-open works from iMessage / SMS / WhatsApp / Instagram DM
// / Gmail / QR code / bookmark / paste / browser refresh — because the
// Emergent hosting layer serves index.html for every non-`/api/*` path
// (SPA fallback), then React Router matches the path here and renders
// <Home />, then Home's PATH_TO_SECTION effect below runs the same
// smooth-scroll convergence utility the hash-nav uses (with its built-in
// hydration wait + retry loop). Adding a new destination = one line here
// plus one <Route path=... element={<Home />} /> below. That's it.
const PATH_TO_SECTION = {
  '/join':           'memberships',
  '/memberships':    'memberships',
  '/trainers':       'trainers',
  '/classes':        'classes',
  '/reviews':        'reviews',
  '/member-app':     'app',
  '/app':            'app',
  '/kid-care':       'kid-care',
  '/kidcare':        'kid-care',
  '/contact':        'contact',
  '/about':          'about',
  '/active-seniors': 'active-adults',
  '/active-adults':  'active-adults',
};

function Home() {
  const [videoOpen, setVideoOpen] = useState(false);
  const location = useLocation();
  useReveal();

  // Honour the URL hash whenever it CHANGES.
  //
  // Why this is the SOLE mechanism for section restoration:
  //   • The Navbar's scroll-spy keeps location.hash in sync with the
  //     section currently in view (replaceState on activeId change).
  //   • So when the user clicks a pricing CTA at the Memberships
  //     section, the URL is `/#memberships`. Then `/membership/x` is
  //     pushed. Pressing browser BACK pops back to `/#memberships`.
  //   • This effect fires on the new hash and smooth-scrolls to the
  //     section. No raw-scrollY restoration is needed — geometry
  //     shifts caused by lazy content / image decode / font swap
  //     don't matter, we always land at the section.
  //
  // Runs for: first mount with hash (deep link), navbar click hash
  // changes, AND POP (BACK / FORWARD). Skipped only when the hash is
  // empty.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const hash = location.hash || window.location.hash;
    if (!hash || hash.length < 2) return undefined;
    // If a click-handler-initiated smooth-scroll is already in flight
    // toward this hash, skip — restarting it here would cancel the
    // in-progress convergence and produce a visible jitter. The
    // Navbar/GlobalAnchorScroll click handlers fire smoothScrollToHash
    // synchronously now (bypassing React's effect-scheduler lag), so
    // this effect's job is mostly POP navigation (browser BACK/
    // FORWARD), deep-link landings, and first-paint hash restoration.
    if (isScrolling()) return undefined;
    // ---- iOS SAFARI HARDENING ----
    // Special-case the topmost section. Loading `/#home` (or any deep
    // link whose target is already within ~140 px of the viewport top)
    // doesn't need a 2-second convergence loop — the user is already
    // there. Running the loop anyway exposed iOS Safari to a memory-
    // pressure crash where the convergence ResizeObserver + a Hero
    // canvas RAF + EmberField mount + Loader unmount all competed for
    // the per-tab budget. Skip when there's nothing to scroll.
    const id0 = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    const targetEl = document.getElementById(id0);
    if (targetEl) {
      const targetTop = targetEl.getBoundingClientRect().top + window.scrollY;
      if (Math.abs(window.scrollY - Math.max(0, targetTop - 80)) < 24) return undefined;
    }
    // Slight defer so the route's first paint completes before we
    // measure target positions (otherwise we'd snap to a target that's
    // about to shift by hundreds of pixels as sections hydrate).
    const id = setTimeout(() => {
      smoothScrollToHash(hash, { history: 'none' });
    }, 50);
    return () => {
      clearTimeout(id);
      cancelActiveScroll();
    };
  }, [location.hash, location.key]);

  // === CLEAN URL PATH → SECTION SCROLL ===
  // Companion to the hash effect above. Fires when the visitor arrives
  // at a clean shareable URL (e.g. /trainers, /join, /kid-care) — those
  // paths all render <Home /> via the routes below. This effect maps
  // the path to a section id and delegates to the SAME smoothScrollToHash
  // utility used by hash-nav, so it inherits the utility's hydration-wait
  // + retry-until-target-exists convergence loop for free — critical when
  // the target section is lazy-mounted (CTA, BottomVideo).
  //
  // The URL itself stays clean (no hash appended) so bookmarks / social
  // shares / QR codes get the canonical shareable form. Only the scroll
  // position is updated. Runs on pathname changes AND on browser
  // BACK/FORWARD (location.key dep) so the whole history stack stays
  // consistent.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const targetId = PATH_TO_SECTION[location.pathname];
    if (!targetId) return undefined; // '/' or any non-mapped path — stay put
    if (isScrolling()) return undefined;
    // Same top-of-page skip as the hash effect — deep links whose target
    // sits within ~140 px of the current scroll position don't need the
    // 2-second convergence loop; running it anyway costs mobile Safari
    // memory pressure for zero visible benefit.
    const el = document.getElementById(targetId);
    if (el) {
      const targetTop = el.getBoundingClientRect().top + window.scrollY;
      if (Math.abs(window.scrollY - Math.max(0, targetTop - 80)) < 24) return undefined;
    }
    const t = setTimeout(() => {
      smoothScrollToHash('#' + targetId, { history: 'none' });
    }, 50);
    return () => {
      clearTimeout(t);
      cancelActiveScroll();
    };
  }, [location.pathname, location.key]);

  // Dev-only: runs once on first paint, surfaces any orphan navigation
  // references (hash → non-existent section id, route → non-registered
  // route, empty `href="#"`). Costs zero in production builds.
  useEffect(() => { runNavigationIntegrityCheck(); }, []);

  return (
    <div className="App grain bg-black text-white" style={{ isolation: 'isolate' }}>
      <Loader />
      <WebsiteFx />
      <CustomCursor />
      <SectionIndex />
      {/* Global ember field — drifts BEHIND every section across the whole
          page. We make it z-index -1 inside the App's isolated stacking
          context so the App's bg-black sits underneath, then the embers,
          then every section (whose own bg is overridden to transparent
          via `.App > main > section { background: transparent }` in
          index.css). Net effect: tiny red embers float in the empty
          space behind images, cards, and text — never on top of them. */}
      <EmberField fixed count={50} flames={14} gym={8} reactive />
      <Navbar />
      <main>
        {/* Above-the-fold + anchor-target sections — eager mount so the
            navbar anchor links (#about, #active-adults, #classes,
            #trainers, #app, #memberships, #contact) always resolve to a
            real element via getElementById, even on first paint before
            the user scrolls. These sections additionally rely on
            `content-visibility: auto` (see index.css `.App > main >
            section`) so their off-screen layout/paint cost is already
            deferred by the browser. */}
        <Hero onPlayVideo={() => setVideoOpen(true)} />
        <HeroDemoStrip />
        <Marquee />
        <Stats />
        <About />
        <ActiveAdults />
        <Classes />
        <Trainers />
        <MemberApp />
        <Memberships />
        <KidCareBanner />
        <Testimonials />
        <Contact />
        {/* Non-anchor tail sections — lazy-mounted via IntersectionObserver
            with a 400px rootMargin so they're already in the DOM before
            the user actually scrolls to them (no pop-in). These two
            sections have NO nav-anchor targets so deferring them is
            100% safe for navigation. They were the heaviest tail
            cost: CTA has a full-bleed background image + animated
            border; BottomVideo lazy-loads a self-hosted MP4. Both are
            pure decoration / call-to-action — not navigation
            destinations. Saves first-paint cost on older iPhones /
            iPads. */}
        <LazyMount minHeight="40vh"><CTA /></LazyMount>
        <LazyMount minHeight="40vh"><BottomVideo /></LazyMount>
      </main>
      <Footer />
      {videoOpen && (
        <Suspense fallback={null}>
          <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
        </Suspense>
      )}
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid rgba(239, 43, 43, 0.4)',
            color: '#fff',
            fontFamily: 'inherit',
            letterSpacing: '0.04em',
          },
        }}
      />
    </div>
  );
}

/**
 * ScrollToTop — forces every NEW client-side route push to land at the
 * top of the new page, AND explicitly restores per-history-entry
 * scroll positions on POP (BACK / FORWARD) navigations. We can't rely
 * on the browser's `scrollRestoration: 'auto'` alone because:
 *   (a) The homepage is a long lazy-loaded SPA — its scroll-height at
 *       navigation-AWAY time often differs from its scroll-height at
 *       navigation-BACK time (content-visibility: auto + reveal-on-
 *       scroll hooks re-collapse some sections, image decode/font
 *       swap shifts others). Browser snapshots a single scrollY; on
 *       restore the visual mapping is wrong.
 *   (b) react-router's hash-based section navigation creates extra
 *       history entries the browser snapshots at scrollY=0.
 *
 * Strategy:
 *   • On every scroll (rAF-throttled) we write `scrollY` into
 *     sessionStorage keyed by the current `location.key`.
 *   • On mount / route-change we check `navigationType`:
 *       - PUSH / REPLACE → scroll to top (or honour hash via Home).
 *       - POP            → look up sessionStorage[location.key] and
 *                          restore. If absent, fall through to top.
 *   • Restoration uses a 3-frame retry (rAF×3) so any layout that
 *     hasn't settled by the first paint still lands the user at the
 *     right pixel.
 */
/**
 * ScrollToTop — controls the scroll position on every route change.
 *
 * Strategy (the one that ACTUALLY works for an SPA with lazy-loaded /
 * content-visibility sections):
 *
 *   • The Navbar's scroll-spy keeps `window.location.hash` in sync
 *     with whichever section is currently in view (via replaceState,
 *     only when the active section changes — see Navbar.jsx).
 *
 *   • So when the user clicks a pricing CTA at the Memberships
 *     section, the URL is already `/#memberships`. The pushState'd
 *     entry that follows is `/membership/x`. Pressing browser BACK
 *     pops back to `/#memberships`.
 *
 *   • Home's hash effect (see Home component) sees `location.hash =
 *     '#memberships'` and smooth-scrolls to that section. No raw-
 *     scrollY snapshot is involved, so lazy-content geometry shifts
 *     between visits don't matter — we always land at the section.
 *
 * This component is now responsible only for the PUSH/REPLACE case
 * (e.g. Link to '/membership/x' from the homepage): scrollTo(0,0)
 * unless there's a hash. POP is fully delegated to Home's hash
 * handler, which re-scrolls to whatever section the URL points at.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    // POP — let the hash-driven Home effect handle section restoration.
    // (If the URL has no hash, the user was at the top of the page, so
    // we still scroll to 0 to keep the visual identity of "back to home".)
    if (navType === 'POP') {
      if (!(hash && hash.length > 1)) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      return;
    }
    // PUSH / REPLACE — hash links scroll to their anchor (Home handles).
    if (hash && hash.length > 1) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      if (window.scrollY !== 0) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [pathname, hash, navType]);
  return null;
}

/**
 * GlobalAnchorScroll — single delegated `click` handler at the document
 * level that intercepts every `<a href="#section-id">` click site-wide
 * and routes it through the shared smoothScroll util (with the 80px
 * navbar offset). Without this, in-section CTAs (Hero, ActiveAdults,
 * CTA section, KidCareBanner, Footer) used the browser's default
 * jump-to-anchor which lands the user 80px BEHIND the fixed Navbar.
 *
 * Behaviour:
 *   • Skips clicks that an upstream handler (e.g. Navbar's own onClick
 *     which already calls smoothScrollToHash) has preventDefault'd.
 *   • Skips modifier-key clicks (cmd/ctrl/middle = open in new tab).
 *   • Only intercepts SAME-PAGE hashes — cross-route navigation handled
 *     by react-router as normal.
 */
function GlobalAnchorScroll() {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;          // primary button only
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.length < 2 || !href.startsWith('#')) return;
      const id = href.slice(1);
      if (!document.getElementById(id)) return;
      e.preventDefault();
      // Same-page same-hash → just re-scroll (no duplicate history entry).
      // Different hash → route through react-router navigate() so iOS
      // Safari's BACK arrow correctly pops back here.
      if (window.location.hash === href) {
        smoothScrollToHash(href, { history: 'none' });
      } else {
        navigate(`/${href}`);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);
  return null;
}

/**
 * Thin red progress bar shown at the very top of the viewport while a
 * lazy-loaded route chunk is in-flight. Better than `fallback={null}`
 * because it tells the user a page change is happening — feels much
 * faster perceptually even though the actual fetch time is unchanged.
 */
function RouteFallback() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        background: 'linear-gradient(90deg, transparent, #ef2b2b 50%, transparent)',
        backgroundSize: '200% 100%',
        animation: 'route-bar-slide 1.1s linear infinite',
      }}
    />
  );
}

function App() {
  // Take over scroll restoration ourselves. We use sessionStorage keyed
  // by react-router's location.key (see ScrollToTop) instead of the
  // browser's `auto` mode, because the homepage's lazy-loaded /
  // content-visibility sections cause its scroll-height to differ
  // between navigation-AWAY and navigation-BACK — which makes the
  // browser's single-snapshot scrollY land the user at the wrong
  // visual position on POP.
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Once the main thread goes idle (or after ~2 s on browsers without
  // requestIdleCallback) — kept as a no-op hook for symmetry; the
  // checkout chunks are now bundled into the main entry so there's
  // nothing to prefetch.
  useEffect(() => {
    /* checkout chunks are statically imported — see top of file */
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <GlobalAnchorScroll />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Clean shareable URLs — each renders the same <Home /> and
              auto-scrolls to its section via PATH_TO_SECTION above.
              Adding a new one = add a line to the map + a line here. */}
          <Route path="/join" element={<Home />} />
          <Route path="/memberships" element={<Home />} />
          <Route path="/trainers" element={<Home />} />
          <Route path="/classes" element={<Home />} />
          <Route path="/reviews" element={<Home />} />
          <Route path="/member-app" element={<Home />} />
          <Route path="/app" element={<Home />} />
          <Route path="/kid-care" element={<Home />} />
          <Route path="/kidcare" element={<Home />} />
          <Route path="/contact" element={<Home />} />
          <Route path="/about" element={<Home />} />
          <Route path="/active-seniors" element={<Home />} />
          <Route path="/active-adults" element={<Home />} />
          <Route path="/membership/:planId" element={<MembershipCheckout />} />
          <Route path="/membership/:planId/info" element={<MembershipInfo />} />
          {/* Catch-all — any unknown path renders <Home /> so a stale
              shared link never lands on a 404 page. Users see the
              homepage instead of a blank screen. */}
          <Route path="*" element={<Home />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
